import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  Paper,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  AlertTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  FormHelperText,
  Checkbox,
  ListItemText,
  OutlinedInput,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Code as CodeIcon,
} from '@mui/icons-material';

export interface OutputField {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'array' | 'object';
  required: boolean;
  description?: string;
}

export interface OutputSchema {
  fields?: OutputField[];
  action_type?: string; // For ACTION type experts (old format - backward compatibility)
  action_types?: string[]; // For ACTION type experts (new format - multiple actions)
  // Support both formats
  required_keys?: string[];
  [key: string]: any; // Detailed schema format
}

interface ActionOption {
  value: string;
  label: string;
  description: string;
}

const DEFAULT_ACTION_OPTIONS: ActionOption[] = [
  { value: 'open_position', label: 'Open Position', description: 'Open a new trading position' },
  { value: 'close_position', label: 'Close Position', description: 'Close an existing position' },
  { value: 'modify_position', label: 'Modify Position', description: 'Modify stop loss or take profit of an existing position' },
  { value: 'cancel_pending_order', label: 'Cancel Pending Order', description: 'Cancel a pending limit/stop order' },
  { value: 'send_alert', label: 'Send Alert', description: 'Send notification or alert' },
  { value: 'test_open_position', label: 'Test Open Position', description: 'Test action handler' },
];

interface OutputSchemaEditorProps {
  value: OutputSchema;
  onChange: (schema: OutputSchema) => void;
  expertType?: 'ANALYSIS' | 'ACTION' | 'AGENT' | 'CODE';
  actionOptions?: ActionOption[];
  actionOptionsLoading?: boolean;
  actionOptionsError?: string;
}

const OutputSchemaEditor: React.FC<OutputSchemaEditorProps> = ({
  value,
  onChange,
  expertType = 'ANALYSIS',
  actionOptions,
  actionOptionsLoading = false,
  actionOptionsError,
}) => {
  const [showJson, setShowJson] = useState(false);
  const osTheme = useTheme();
  const isMobile = useMediaQuery(osTheme.breakpoints.down('sm'));
  const selectedActionTypes = value.action_types || (value.action_type ? [value.action_type] : []);
  const resolvedActionOptions = actionOptions ?? DEFAULT_ACTION_OPTIONS;
  const hasAvailableActionOptions = resolvedActionOptions.length > 0;

  // Convert current schema to fields format
  const getFieldsFromSchema = (schemaValue: OutputSchema): OutputField[] => {
    if (schemaValue.fields) {
      return schemaValue.fields;
    }
    
    if (schemaValue.required_keys) {
      return schemaValue.required_keys.map(key => ({
        name: key,
        type: 'string' as const,
        required: true,
      }));
    }

    // Handle JSON Schema format: {type: "object", properties: {...}, required: [...]}
    if (schemaValue.type === 'object' && schemaValue.properties) {
      const fields: OutputField[] = [];
      const requiredFields = schemaValue.required || [];
      
      Object.entries(schemaValue.properties).forEach(([key, prop]: [string, any]) => {
        fields.push({
          name: key,
          type: prop.type || 'string',
          required: requiredFields.includes(key),
          description: prop.description,
        });
      });
      
      return fields;
    }

    // Extract from detailed schema format (legacy)
    const fields: OutputField[] = [];
    Object.keys(schemaValue).forEach(key => {
      // Skip action type fields and JSON Schema meta fields when extracting
      if (key !== 'action_type' && key !== 'action_types' && key !== 'type' && key !== 'properties' && key !== 'required' && typeof schemaValue[key] === 'object' && schemaValue[key].type) {
        fields.push({
          name: key,
          type: schemaValue[key].type || 'string',
          required: schemaValue[key].required !== false,
          description: schemaValue[key].description,
        });
      }
    });

    return fields;
  };

  const [fields, setFields] = useState<OutputField[]>(() => getFieldsFromSchema(value));
  
  // Track if we're making internal changes to avoid useEffect loops
  const isInternalChangeRef = useRef(false);

  // Update fields when value prop changes (e.g., when loading an expert)
  useEffect(() => {
    // Skip if this is an internal change (we triggered the onChange ourselves)
    if (isInternalChangeRef.current) {
      console.log('🔄 OutputSchemaEditor: Skipping useEffect (internal change)');
      isInternalChangeRef.current = false;
      return;
    }
    
    console.log('🔄 OutputSchemaEditor: value changed externally', value);
    const newFields = getFieldsFromSchema(value);
    console.log('🔄 OutputSchemaEditor: extracted fields', newFields);
    setFields(newFields);
  }, [value]);

  const handleAddField = () => {
    console.log('➕ Adding new field. Current fields:', fields);
    const newFields = [
      ...fields,
      { name: '', type: 'string' as const, required: true },
    ];
    console.log('➕ New fields array:', newFields);
    setFields(newFields);
    updateSchema(newFields);
  };

  const handleRemoveField = (index: number) => {
    const newFields = fields.filter((_, i) => i !== index);
    setFields(newFields);
    updateSchema(newFields);
  };

  const handleFieldChange = (index: number, field: Partial<OutputField>) => {
    const newFields = fields.map((f, i) => (i === index ? { ...f, ...field } : f));
    setFields(newFields);
    updateSchema(newFields);
  };

  const updateSchema = (updatedFields: OutputField[]) => {
    console.log('📝 updateSchema called');
    console.log('📝 Fields to save:', updatedFields);

    // Filter out fields with empty names - they're still being edited
    const namedFields = updatedFields.filter(f => f.name.trim() !== '');
    console.log('📝 Named fields (will be saved):', namedFields);

    // Always use JSON Schema format to match backend expectations
    const properties: Record<string, any> = {};
    const required: string[] = [];

    namedFields.forEach(field => {
      properties[field.name] = {
        type: field.type || 'string',
        ...(field.description && { description: field.description }),
      };

      if (field.required) {
        required.push(field.name);
      }
    });

    const newSchema: OutputSchema = {
      type: 'object',
      properties,
      required,
    };

    // Preserve action_types/action_type for ACTION experts
    if (value.action_types) {
      newSchema.action_types = value.action_types;
    } else if (value.action_type) {
      newSchema.action_type = value.action_type;
    }

    console.log('📝 Final schema to save (JSON Schema format):', newSchema);

    // Mark as internal change so useEffect doesn't reset our local fields state
    isInternalChangeRef.current = true;
    onChange(newSchema);
  };

  const handleActionTypeChange = (actionType: string) => {
    onChange({ ...value, action_type: actionType });
  };

  const handleActionTypesChange = (event: any) => {
    const selectedTypes = event.target.value as string[];
    // Remove old action_type field and use new action_types
    const newValue = { ...value };
    delete newValue.action_type;
    newValue.action_types = selectedTypes;
    onChange(newValue);
  };

  const getJsonPreview = () => {
    return JSON.stringify(value, null, 2);
  };

  const handleJsonEdit = (jsonStr: string) => {
    try {
      const parsed = JSON.parse(jsonStr);
      onChange(parsed);
      setFields(getFieldsFromSchema(parsed));
    } catch (e) {
      // Invalid JSON, ignore
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Output Schema Configuration
          <Tooltip title="Define the expected output structure from this expert">
            <InfoIcon fontSize="small" color="action" />
          </Tooltip>
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton size="small" onClick={() => setShowJson(!showJson)}>
            <CodeIcon />
          </IconButton>
        </Box>
      </Box>

      {expertType === 'ACTION' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>Action Types Required</AlertTitle>
          ACTION experts must specify one or more action types that the LLM can choose from when executing.
        </Alert>
      )}

      {expertType === 'AGENT' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>Agent Output</AlertTitle>
          AGENT experts delegate execution to an agent with tools and memory. The output schema here 
          defines the expected structure of the agent's response. The agent will attempt to return 
          results matching this schema, but may include additional context from tool executions.
        </Alert>
      )}

      {expertType === 'CODE' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>Code Output</AlertTitle>
          CODE experts execute Python code directly. Your code should return a dictionary with keys 
          matching the fields defined in the output schema below. Define the expected output structure 
          to validate and document what your code produces.
        </Alert>
      )}

      {expertType === 'ACTION' && (
        <FormControl fullWidth required sx={{ mb: 2 }}>
          <InputLabel id="action-types-label">Action Types</InputLabel>
          <Select
            labelId="action-types-label"
            multiple
            value={selectedActionTypes}
            onChange={handleActionTypesChange}
            input={<OutlinedInput label="Action Types" />}
            disabled={actionOptionsLoading || !hasAvailableActionOptions}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as string[]).map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {resolvedActionOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Checkbox checked={selectedActionTypes.indexOf(option.value) > -1} />
                <ListItemText primary={option.label} secondary={option.description} />
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {actionOptionsLoading
              ? 'Loading available action adapters...'
              : !hasAvailableActionOptions
                ? 'No action adapters are available for your current subscription.'
                : 'Select one or more action types. The LLM will choose the appropriate action based on the analysis.'}
          </FormHelperText>
          {actionOptionsError && (
            <FormHelperText error>{actionOptionsError}</FormHelperText>
          )}
        </FormControl>
      )}

      {showJson ? (
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            multiline
            rows={15}
            value={getJsonPreview()}
            onChange={(e) => handleJsonEdit(e.target.value)}
            label="JSON Schema Editor"
            helperText="Edit the schema directly as JSON"
            sx={{ fontFamily: 'monospace' }}
          />
        </Box>
      ) : (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            Validates keys, types, and optionality. Descriptions are included in the prompt to guide the model and document the expected output.
          </Alert>

          <Box sx={{ mb: 2 }}>
            {fields.map((field, index) => (
              <Paper key={index} sx={{ p: isMobile ? 1.5 : 2, mb: 2 }}>
                <Box sx={{ display: 'flex', gap: isMobile ? 1 : 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <TextField
                    label="Field Name"
                    value={field.name}
                    onChange={(e) =>
                      handleFieldChange(index, { name: e.target.value })
                    }
                    placeholder="e.g., sentiment, confidence, signal"
                    size={isMobile ? 'small' : 'medium'}
                    sx={{ flex: 1, minWidth: isMobile ? '100%' : 120 }}
                    required
                  />

                  <FormControl sx={{ minWidth: isMobile ? 100 : 150 }} size={isMobile ? 'small' : 'medium'}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={field.type}
                      label="Type"
                      onChange={(e) =>
                        handleFieldChange(index, {
                          type: e.target.value as OutputField['type'],
                        })
                      }
                    >
                      <MenuItem value="string">String</MenuItem>
                      <MenuItem value="number">Number</MenuItem>
                      <MenuItem value="boolean">Boolean</MenuItem>
                      <MenuItem value="array">Array</MenuItem>
                      <MenuItem value="object">Object</MenuItem>
                    </Select>
                  </FormControl>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: isMobile ? 0 : 'auto' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.required}
                          size={isMobile ? 'small' : 'medium'}
                          onChange={(e) =>
                            handleFieldChange(index, { required: e.target.checked })
                          }
                        />
                      }
                      label="Required"
                    />

                    <IconButton
                      color="error"
                      onClick={() => handleRemoveField(index)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>

                {/* Always show description field - it's included in the prompt to guide the model */}
                <TextField
                  fullWidth
                  label="Description (optional)"
                  value={field.description || ''}
                  onChange={(e) =>
                    handleFieldChange(index, { description: e.target.value })
                  }
                  placeholder="Describe what this field contains"
                  sx={{ mt: isMobile ? 1 : 2 }}
                  size="small"
                  helperText="Descriptions are included in the prompt to guide the model's output"
                />
              </Paper>
            ))}

            <Button
              startIcon={<AddIcon />}
              onClick={handleAddField}
              variant="outlined"
              fullWidth
            >
              Add Output Field
            </Button>
          </Box>
        </>
      )}

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Example Output Format</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
            {expertType === 'ACTION' && (value.action_types || value.action_type)
              ? `{
  "action_type": "${(value.action_types && value.action_types.length > 0) ? value.action_types[0] : value.action_type}",${value.action_types && value.action_types.length > 1 ? ' // Choose from: ' + value.action_types.join(', ') : ''}
${fields.filter(f => f.name).map(f => `  "${f.name}": ${
  f.type === 'string' ? '"example value"' :
  f.type === 'number' ? '42' :
  f.type === 'boolean' ? 'true' :
  f.type === 'array' ? '[]' :
  '{}'
}`).join(',\n')}
}`
              : `{
${fields.filter(f => f.name).map(f => `  "${f.name}": ${
  f.type === 'string' ? '"example value"' :
  f.type === 'number' ? '42' :
  f.type === 'boolean' ? 'true' :
  f.type === 'array' ? '[]' :
  '{}'
}`).join(',\n')}
}`}
          </Typography>
        </AccordionDetails>
      </Accordion>

      {fields.length === 0 && !value.action_types && !value.action_type && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          No output schema defined. The expert will accept any output without validation.
        </Alert>
      )}
    </Box>
  );
};

export default OutputSchemaEditor;
